import {FormState, FormStateWithIssues} from '@raison/form-models';
import {useLocationState} from '@raison/navigation';
import {ErroneousServerResult} from '@raison/network-facade';
import {TransactionId} from '@raison/types';
import {RootLayout, RsnAmount, RsnButton, RsnDialog, RsnInput, RsnMessage, RsnSkeleton} from '@raison/ui-kit';
import {useQuery} from '@tanstack/react-query';
import * as Schema from 'effect/Schema';
import React, {useReducer} from 'react';
import {useNavigate} from 'react-router';
import {useCurrencyFormat} from '~/entities/currencies';
import {OperationDetail, OperationInfo} from '~/entities/operations-fiat';
import {useSwiftBankInfo} from '~/processes/fiat-operations/api/withdraw/swiftBankInfo';
import {useSwiftRequisitesOptions} from '~/processes/fiat-operations/api/withdraw/swiftRequisites';
import {LabelWithInfo} from '~/processes/fiat-operations/ui/LabelWithInfo';
import {OperableAccount, OperableBank} from '~/redux/finances/domain';
import {useWithdrawFeeOptions} from '../../api/withdraw/withdrawFee';
import {useWithdrawSwiftOrderMutation} from '../../api/withdraw/withdrawSwiftOrder';
import {formatAccountNumber, formatSwift, withFormatting} from '../../lib/formattedInput';
import {TotalReceiveDialog} from '../Shared/TotalReceiveDialog';

type WithdrawSwiftFormState = ReturnType<typeof WithdrawSwiftFormState>;
const WithdrawSwiftFormState = (initial: {swift: string; account: string} | null) =>
    FormState.from({
        swift: initial?.swift ? formatSwift(initial.swift) : '',
        account: initial?.account ? formatAccountNumber(initial.account) : '',
    });

type Values = FormState.inferValues<WithdrawSwiftFormState>;

export type SwiftRequisitesProps = {
    amount: RsnAmount;
    account: OperableAccount;
    onSuccess: (id: TransactionId) => void;
    onProblem: (problem: ErroneousServerResult) => void;
    method: OperableBank;
};

export function WithdrawSwiftRequisites({account, method, amount, onSuccess, onProblem}: SwiftRequisitesProps) {
    const navigate = useNavigate();

    const {data: {value: requisites} = {}, isLoading: isRequisitesLoading} = useQuery(
        useSwiftRequisitesOptions(account.id)
    );

    const [form, dispatchForm] = useReducer(
        FormState.reduce<Values>,
        {
            swift: requisites?.swift || '',
            account: requisites?.account || '',
        },
        WithdrawSwiftFormState
    );

    const {format: formatAsSubject} = useCurrencyFormat(account);

    const {data: {value: fee} = {}, isLoading: isFeeLoading} = useQuery(
        useWithdrawFeeOptions({
            accountId: account.id,
            methodId: method?.id,
            amount: RsnAmount.toString(amount) || '0',
        })
    );

    const {
        isDebouncing: isBicInfoDebouncing,
        data: {value: bicData, problem: bicErrors} = {},
        isLoading: isBicInfoLoading,
    } = useSwiftBankInfo(account.id, form.fields.swift.value);

    const {mutateAsync, isPending: isMutationPending} = useWithdrawSwiftOrderMutation(account.id);

    const t = {
        feeLabel: 'Fee',
        amountLabel: 'Total amount',
        totalLabel: (() => {
            if (fee == null) {
                return 'Total';
            }
            return fee.commissionAbove ? 'Total spend' : 'Total receive';
        })(),
        required: 'This field is required',
        invalidSwift: 'SWIFT is not valid',
        invalidAccount: 'Account number is not valid',
        bankNameLabel: 'Bank name',
    };

    const translateServerIssue = (issue: string) => {
        if (/should not be blank/i.test(issue)) {
            return t.required;
        }
        if (/8 or 11 character/i.test(issue)) {
            return t.invalidSwift;
        }
        return issue;
    };

    const formWithIssues = FormStateWithIssues.fromRecipe(form, issues => {
        /**
         * @see https://gitlab.raison.digital/raison/rest-api/-/blob/9656613169e3a647fef400281afdcdc3fc303a09/src/Validator/Constraint/AccountNumber.php
         */
        if (!/^[a-zA-Z0-9 №._-]+$/.test(form.fields.account.value)) {
            issues.account = t.invalidAccount;
        }

        if (form.fields.account.value === '') {
            issues.account = t.required;
        }

        if (form.fields.swift.value === '') {
            issues.swift = t.required;
        } else if (bicErrors?.bic) {
            issues.swift = translateServerIssue(bicErrors.bic);
        }
    });

    const {fields, isSubmitDisabled, onSubmit} = FormState.selectFormView(formWithIssues, {
        dispatch: dispatchForm,
        async submit() {
            const {value, problem} = await mutateAsync({
                amount: RsnAmount.toString(amount),
                swift: fields.swift.value,
                account: fields.account.value,
            });
            if (problem) {
                onProblem(problem);
                return false;
            }
            onSuccess(value.id as TransactionId);
            return true;
        },
    });

    const [dialog, setDialog] = useLocationState('withdraw-swift-requisites-dialog', Schema.Literal('total'));
    const closeDialog = () => navigate(-1);

    const canSubmit =
        !isBicInfoLoading && !isBicInfoDebouncing && !isSubmitDisabled && !isMutationPending && !isFeeLoading;

    return (
        <RootLayout title={'Worldwide withdraw'}>
            <form className={'flex flex-1 flex-col gap-6 p-4'}>
                <RsnMessage.FootnoteV2>
                    Receiving bank account must be named on the account holder of Raison account, otherwise your bank
                    will return the payment.
                </RsnMessage.FootnoteV2>

                <OperationInfo label={'Beneficiary name'}>
                    {isRequisitesLoading ? <RsnSkeleton width={100} height={20} /> : requisites?.beneficiary_name}
                </OperationInfo>
                <OperationInfo label={'Beneficiary address'}>
                    {isRequisitesLoading ? <RsnSkeleton width={'100%'} height={20} /> : requisites?.beneficiary_address}
                </OperationInfo>

                <RsnInput label={'Account number'} {...withFormatting(fields.account, formatAccountNumber)} />
                <RsnInput label={'SWIFT'} {...withFormatting(fields.swift, formatSwift)} />
                <div className={'mt-auto flex flex-col gap-4'}>
                    <OperationDetail.Layout>
                        <OperationDetail label={t.bankNameLabel}>{bicData?.name ?? '—'}</OperationDetail>
                    </OperationDetail.Layout>
                    <OperationDetail.Layout>
                        {isFeeLoading ? (
                            <>
                                <OperationDetail label={t.feeLabel}>
                                    <RsnSkeleton width={60} height={16} />
                                </OperationDetail>
                                <OperationDetail label={t.amountLabel}>
                                    <RsnSkeleton width={60} height={16} />
                                </OperationDetail>
                                <OperationDetail label={t.totalLabel}>
                                    <RsnSkeleton width={60} height={16} />
                                </OperationDetail>
                            </>
                        ) : (
                            <>
                                <OperationDetail label={t.feeLabel}>{formatAsSubject(fee?.fee ?? 0)}</OperationDetail>
                                <OperationDetail label={t.amountLabel}>
                                    {formatAsSubject(fee?.inputAmount ?? 0)}
                                </OperationDetail>
                                <OperationDetail.Total
                                    label={
                                        <LabelWithInfo onClick={() => setDialog('total')}>{t.totalLabel}</LabelWithInfo>
                                    }
                                >
                                    {formatAsSubject(fee?.total ?? 0)}
                                </OperationDetail.Total>
                            </>
                        )}
                    </OperationDetail.Layout>
                    <RsnButton type={'submit'} disabled={!canSubmit} onClick={onSubmit}>
                        Submit
                    </RsnButton>
                </div>
            </form>
            <RsnDialog.Controller onClose={closeDialog}>
                {dialog === 'total' && <TotalReceiveDialog />}
            </RsnDialog.Controller>
        </RootLayout>
    );
}
